import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { NavLink, useLocation } from 'react-router-dom'
import axios from 'axios'
import jwt_decode from 'jwt-decode'
import config from '../../../config'
import Logo from '../../images/blossom-logo.jpg'
import sections, { canSee } from './menuItems'
import {
  buildPaymentPlan,
  normalizeFlat,
  RECEIPTS_CHANGED_EVENT
} from '../../../features/payments/dummyPayments'

const COMPACT_KEY = 'bl-nav-compact'
const OPEN_KEY = 'bl-nav-open-sections'
const PHONE_QUERY = '(max-width: 767.98px)'

const readJSON = (key, fallback) => {
  try {
    const v = localStorage.getItem(key)
    return v === null ? fallback : JSON.parse(v)
  } catch (e) {
    return fallback
  }
}
const writeJSON = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)) } catch (e) {}
}

// Logged-in user's name / role straight from the token (for the profile card)
const readUser = () => {
  try {
    const t = jwt_decode(localStorage.getItem('token'))
    return { name: t.Name || 'Member', role: t.Role || '' }
  } catch (e) {
    return { name: 'Member', role: '' }
  }
}

const initials = name =>
  (name || '?').split(' ').filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('')

// Allow long labels like "Circular/Notices/Minutes" to wrap after each slash
const wrapSlashes = text =>
  text.split('/').map((part, i, arr) => (
    <React.Fragment key={i}>{part}{i < arr.length - 1 && <>/<wbr /></>}</React.Fragment>
  ))

// Tracks a media query
const useMediaQuery = query => {
  const get = () => typeof window !== 'undefined' && window.matchMedia(query).matches
  const [matches, setMatches] = useState(get)
  useEffect(() => {
    const mql = window.matchMedia(query)
    const onChange = () => setMatches(mql.matches)
    mql.addEventListener ? mql.addEventListener('change', onChange) : mql.addListener(onChange)
    // some browsers/emulators skip the media-query change event on resize
    window.addEventListener('resize', onChange)
    onChange()
    return () => {
      mql.removeEventListener ? mql.removeEventListener('change', onChange) : mql.removeListener(onChange)
      window.removeEventListener('resize', onChange)
    }
  }, [query])
  return matches
}

// Payment-due badge for members (dummy payments module)
let flatPromise = null
const usePaymentDue = enabled => {
  const [due, setDue] = useState(null)
  useEffect(() => {
    if (!enabled) return
    let alive = true
    if (!flatPromise) {
      flatPromise = axios
        .get(`${config.apiUrl}/flat-details`, { headers: { Authorization: localStorage.getItem('token') } })
        .then(res => normalizeFlat(res.data?.data))
        .catch(() => null)
    }
    const evaluate = () =>
      flatPromise.then(flat => {
        if (alive) setDue(flat ? buildPaymentPlan(flat)?.nextDue || null : null)
      })
    evaluate()
    window.addEventListener(RECEIPTS_CHANGED_EVENT, evaluate)
    return () => {
      alive = false
      window.removeEventListener(RECEIPTS_CHANGED_EVENT, evaluate)
    }
  }, [enabled])
  return due
}

const SideNav = ({ userAccess }) => {
  const { pathname } = useLocation()
  const isPhone = useMediaQuery(PHONE_QUERY)
  const [compact, setCompact] = useState(() => readJSON(COMPACT_KEY, false))
  const [openSections, setOpenSections] = useState(() => readJSON(OPEN_KEY, { society: true, 'my-flat': true, admin: true }))
  const user = readUser()

  const isAdmin = Array.isArray(userAccess) && userAccess.includes('ListUser')
  const paymentDue = usePaymentDue(!isAdmin)
  const rail = isPhone || compact

  // Width lives on <html> so the header and content can offset themselves
  useEffect(() => {
    document.documentElement.classList.toggle('bl-rail', rail)
    return () => document.documentElement.classList.remove('bl-rail')
  }, [rail])

  const toggleCompact = () => {
    setCompact(c => {
      writeJSON(COMPACT_KEY, !c)
      return !c
    })
  }

  const toggleSection = id => {
    setOpenSections(prev => {
      const next = { ...prev, [id]: !prev[id] }
      writeJSON(OPEN_KEY, next)
      return next
    })
  }

  const visible = sections
    .filter(s => canSee(s, userAccess))
    .map(s => ({ ...s, items: s.items.filter(i => canSee(i, userAccess)) }))

  const badgeFor = item => {
    if (item.badge === 'paymentDue' && paymentDue) {
      return {
        tone: paymentDue.status === 'overdue' ? 'danger' : 'warning',
        text: paymentDue.status === 'overdue' ? 'Overdue' : 'Due'
      }
    }
    return null
  }

  const linkClass = ({ isActive }) => `bl-sidenav__link${isActive ? ' active' : ''}`

  const renderLink = item => {
    const badge = badgeFor(item)
    return (
      <NavLink
        to={item.to}
        end={item.end}
        className={linkClass}
        title={rail ? item.label : undefined}
        aria-label={badge ? `${item.label} (${badge.text})` : undefined}
      >
        <span className="bl-sidenav__icon">
          <ion-icon name={item.icon} aria-hidden="true"></ion-icon>
          {badge && rail && <span className={`bl-sidenav__dot is-${badge.tone}`} aria-hidden="true"></span>}
        </span>
        <span className="bl-sidenav__label">{wrapSlashes(item.label)}</span>
        {badge && !rail && <span className={`bl-sidenav__badge is-${badge.tone}`} aria-hidden="true">{badge.text}</span>}
        {item.short && <span className="bl-sidenav__short" aria-hidden="true">{item.short}</span>}
      </NavLink>
    )
  }

  return (
    <nav className={`bl-sidenav${rail ? ' is-rail' : ''}`} aria-label="Main">
      <div className="bl-sidenav__brand">
        <img src={Logo} alt="Blossom Society" width="36" height="36" />
        {!rail && (
          <div className="bl-sidenav__brand-text" aria-hidden="true">
            <span className="bl-sidenav__brand-name">Blossom Society</span>
            <span className="bl-sidenav__brand-sub">Residents portal</span>
          </div>
        )}
      </div>

      <div className="bl-sidenav__scroll">
        {visible.map(section => {
          const sectionActive = section.items.some(i => i.to === pathname)
          const open = openSections[section.id] !== false

          // ---- rail: grouped sections become one fly-out icon ----
          if (rail && section.railGroup) {
            const groupBadge = section.items.map(badgeFor).find(Boolean)
            return (
              <ul key={section.id} className="bl-sidenav__list">
                <li className="dropend">
                  <button
                    type="button"
                    className={`bl-sidenav__link${sectionActive ? ' active' : ''}`}
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    aria-haspopup="menu"
                    aria-label={groupBadge ? `${section.title} (${groupBadge.text})` : section.title}
                    title={section.title}
                  >
                    <span className="bl-sidenav__icon">
                      <ion-icon name={section.icon} aria-hidden="true"></ion-icon>
                      {groupBadge && <span className={`bl-sidenav__dot is-${groupBadge.tone}`} aria-hidden="true"></span>}
                    </span>
                    <span className="bl-sidenav__short" aria-hidden="true">{section.short}</span>
                  </button>
                  <ul className="dropdown-menu bl-sidenav__flyout">
                    <li><h6 className="dropdown-header">{section.title}</h6></li>
                    {section.items.map(item => {
                      const badge = badgeFor(item)
                      return (
                        <li key={item.label}>
                          <NavLink
                            to={item.to}
                            className={({ isActive }) => `dropdown-item${isActive ? ' active' : ''}`}
                          >
                            <ion-icon name={item.icon} aria-hidden="true"></ion-icon>
                            {item.label}
                            {badge && <span className={`bl-sidenav__badge is-${badge.tone} ms-auto`}>{badge.text}</span>}
                          </NavLink>
                        </li>
                      )
                    })}
                  </ul>
                </li>
              </ul>
            )
          }

          // ---- rail: flat sections show each item as an icon ----
          if (rail) {
            return (
              <ul key={section.id} className="bl-sidenav__list">
                {section.items.map(item => <li key={item.label}>{renderLink(item)}</li>)}
              </ul>
            )
          }

          // ---- full sidebar: collapsible titled section ----
          const listId = `nav-section-${section.id}`
          return (
            <div key={section.id} className="bl-sidenav__section">
              <button
                type="button"
                className={`bl-sidenav__section-title${sectionActive && !open ? ' has-active' : ''}`}
                aria-expanded={open}
                aria-controls={listId}
                onClick={() => toggleSection(section.id)}
              >
                {section.title}
                <ion-icon name="chevron-down-outline" aria-hidden="true" class={open ? 'is-open' : ''}></ion-icon>
              </button>
              {open && (
                <ul id={listId} className="bl-sidenav__list">
                  {section.items.map(item => <li key={item.label}>{renderLink(item)}</li>)}
                </ul>
              )}
            </div>
          )
        })}
      </div>

      <div className="bl-sidenav__footer">
        {!rail && (
          <div className="bl-sidenav__user">
            <span className="bl-avatar" aria-hidden="true">{initials(user.name)}</span>
            <span className="bl-sidenav__user-text">
              <span className="bl-sidenav__user-name">{user.name}</span>
              {user.role && <span className="bl-sidenav__user-role">{user.role}</span>}
            </span>
          </div>
        )}
        {!isPhone && (
          <button
            type="button"
            className="bl-sidenav__link bl-sidenav__collapse"
            onClick={toggleCompact}
            aria-pressed={compact}
            aria-label={compact ? 'Expand sidebar' : 'Collapse sidebar'}
            title={compact ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <span className="bl-sidenav__icon">
              <ion-icon name={compact ? 'chevron-forward-outline' : 'chevron-back-outline'} aria-hidden="true"></ion-icon>
            </span>
            <span className="bl-sidenav__label">Collapse</span>
          </button>
        )}
      </div>
    </nav>
  )
}

const mapStateToProps = ({ auth }) => ({
  userAccess: auth.userAccess
})

export default connect(mapStateToProps)(SideNav)
